import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";

const THUMB_LRU_MAX = 64;
/** @type {Map<string, 'ok' | 'fail'>} */
const thumbStatusLru = new Map();

function thumbLruGet(url) {
  if (!url || !thumbStatusLru.has(url)) return null;
  const status = thumbStatusLru.get(url);
  thumbStatusLru.delete(url);
  thumbStatusLru.set(url, status);
  return status;
}

function thumbLruSet(url, status) {
  if (!url) return;
  if (thumbStatusLru.has(url)) thumbStatusLru.delete(url);
  thumbStatusLru.set(url, status);
  while (thumbStatusLru.size > THUMB_LRU_MAX) {
    const oldest = thumbStatusLru.keys().next().value;
    thumbStatusLru.delete(oldest);
  }
}

/**
 * Resolves a safe image URL. Kept for public API compatibility.
 * No redundant network probe — callers that render <img> should use GaImage.
 */
export function useSafeImage(src, fallback) {
  const [current, setCurrent] = useState(() => {
    if (!src) return fallback || "";
    if (thumbLruGet(src) === "fail") return fallback || "";
    return src;
  });

  useEffect(() => {
    if (!src) {
      setCurrent(fallback || "");
      return;
    }
    if (thumbLruGet(src) === "fail") {
      setCurrent(fallback || "");
      return;
    }
    setCurrent(src);
  }, [src, fallback]);

  return current || fallback || "";
}

export function GaImage({
  src,
  alt = "",
  eager = false,
  width,
  height,
  fallback,
  sx,
  ...rest
}) {
  const cached = src ? thumbLruGet(src) : null;
  const [failedSrc, setFailedSrc] = useState(
    () => (cached === "fail" && src ? src : ""),
  );
  const current =
    !src || (fallback && failedSrc === src) ? fallback || "" : src;

  useEffect(() => {
    if (!src) {
      setFailedSrc("");
      return;
    }
    if (thumbLruGet(src) === "fail") {
      setFailedSrc(src);
      return;
    }
    setFailedSrc("");
  }, [src]);

  const markFailed = () => {
    if (!src) return;
    thumbLruSet(src, "fail");
    if (fallback && failedSrc !== src) setFailedSrc(src);
  };

  const markOk = () => {
    if (src && current === src) thumbLruSet(src, "ok");
  };

  return (
    <Box
      component="img"
      alt={alt}
      width={width}
      height={height}
      draggable={false}
      decoding="async"
      {...rest}
      ref={(node) => {
        if (!node || current !== src || !src) return;
        if (node.dataset.fallbackBound !== src) {
          node.dataset.fallbackBound = src;
          node.addEventListener("error", () => markFailed());
        }
        if (node.complete && node.naturalWidth === 0) markFailed();
        else if (node.complete && node.naturalWidth > 0) markOk();
      }}
      src={current || undefined}
      loading={eager ? "eager" : "lazy"}
      fetchpriority={eager ? "high" : "low"}
      onLoad={(event) => {
        markOk();
        rest.onLoad?.(event);
      }}
      onError={(event) => {
        markFailed();
        rest.onError?.(event);
      }}
      sx={{
        backgroundColor: "#0D0F0A",
        ...(width && height
          ? { aspectRatio: `${Number(width)} / ${Number(height)}` }
          : null),
        ...sx,
      }}
    />
  );
}
